/**
 * Pictures of the till, for the picture guide.
 *
 * Drawn from the till's own parts — the real `Button`, the same tile and chip
 * colours — rather than from screenshots, so a picture never shows a button the
 * till no longer has, and the guide still works with no signal. Everything here
 * is decoration for a sentence that says the same thing, so it is hidden from
 * screen readers and cannot be clicked.
 */

import { Check, Hand, Wifi, WifiOff, X } from "lucide-react";
import type { ComponentType, ReactNode } from "react";

import { Button, type ButtonProps } from "@/components/ui";
import { cn } from "@/lib/cn";

/** The till's screen: a frame the scene sits in. */
export function Screen({
  children,
  className,
  big,
}: {
  children: ReactNode;
  className?: string;
  /** Alone on the page, with no map beside it: drawn larger. */
  big?: boolean;
}) {
  return (
    <div
      aria-hidden
      inert
      className={cn(
        "pointer-events-none relative mx-auto grid min-h-56 w-full max-w-[26rem] select-none place-items-center gap-3 rounded-[22px] border-[6px] border-navy-900 bg-canvas px-4 py-6 shadow-raised print:min-h-0 print:shadow-none print:[zoom:0.75]",
        big && "sm:[zoom:1.25]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** "Tap here": a pulsing ring round the target and a finger on it. */
export function Tap({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("relative inline-flex", className)}>
      <span className="absolute -inset-2 rounded-[14px] ring-4 ring-gold-500 motion-safe:animate-pulse" />
      {children}
      <Hand
        className="absolute -bottom-6 -right-4 size-10 rotate-[-20deg] fill-white text-navy-900 drop-shadow"
        strokeWidth={1.75}
      />
    </span>
  );
}

/** A real till button, as a picture. */
export function Btn(props: ButtonProps) {
  return <Button tabIndex={-1} {...props} />;
}

/** A product on the till's grid, with a drawing where the pack shot goes. */
export function Tile({
  name,
  price,
  hue,
  icon: Icon,
}: {
  name: string;
  price: string;
  hue: number;
  icon: ComponentType<{ className?: string }>;
}) {
  return (
    <span className="flex w-24 flex-col overflow-hidden rounded-card border border-line bg-surface text-left">
      <span
        className="grid aspect-[4/3] place-items-center"
        style={{ backgroundColor: `hsl(${hue} 70% 94%)`, color: `hsl(${hue} 55% 32%)` }}
      >
        <Icon className="size-9" />
      </span>
      <span className="px-2 pb-2 pt-1.5">
        <span className="line-clamp-1 block text-xs font-medium text-ink-900">{name}</span>
        <span className="text-sm font-semibold text-brand-600">{price}</span>
      </span>
    </span>
  );
}

/** The basket on the right of the till. */
export function Basket({ lines, total }: { lines: [string, number, string][]; total: string }) {
  return (
    <div className="w-full max-w-[17rem] rounded-card border border-line bg-surface p-3 text-left shadow-card">
      <p className="mb-2 text-sm font-semibold text-ink-900">Current sale</p>
      <ul className="grid gap-1.5 text-sm">
        {lines.map(([name, qty, price]) => (
          <li key={name} className="flex justify-between gap-2">
            <span className="truncate">
              <span className="font-numeric text-ink-500">{qty}×</span> {name}
            </span>
            <span className="font-numeric text-ink-900">{price}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 flex justify-between border-t border-line pt-2 font-bold text-ink-900">
        <span>Total</span>
        <span className="font-numeric">{total}</span>
      </p>
    </div>
  );
}

/** The status chip at the top of the till. */
export function Chip({ online, waiting = 0 }: { online: boolean; waiting?: number }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-pill px-4 py-2 text-base font-semibold",
        online ? "bg-money-50 text-money-700" : "bg-warn-50 text-warn-700",
      )}
    >
      {online ? <Wifi className="size-5" /> : <WifiOff className="size-5" />}
      {online ? "Online" : "Offline"}
      {waiting > 0 && (
        <span className="grid h-6 min-w-6 place-items-center rounded-pill bg-current/15 px-1.5 text-sm">
          {waiting}
        </span>
      )}
    </span>
  );
}

/** A text box with something typed in it. */
export function Field({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <span className={cn("grid gap-1 text-left", wide ? "w-full max-w-[17rem]" : "w-48")}>
      <span className="text-xs font-medium text-ink-600">{label}</span>
      <span className="flex h-11 items-center rounded-control border-2 border-brand-500 bg-surface px-3 text-base font-semibold text-ink-900">
        {value}
        <span className="ml-0.5 h-5 w-0.5 bg-ink-900 motion-safe:animate-pulse" />
      </span>
    </span>
  );
}

/** PIN dots, filled as the cashier types. */
export function PinDots({ filled, of = 4 }: { filled: number; of?: number }) {
  return (
    <span className="flex gap-3">
      {Array.from({ length: of }, (_, i) => (
        <span
          key={i}
          className={cn(
            "size-5 rounded-full border-2 border-navy-900",
            i < filled && "bg-navy-900",
          )}
        />
      ))}
    </span>
  );
}

/** The PIN pad: three keys a row, as on the till. */
export function Keypad({ pressed }: { pressed?: string }) {
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", ""];
  return (
    <span className="grid grid-cols-[repeat(3,3rem)] gap-1.5">
      {keys.map((k, i) =>
        k ? (
          <span
            key={i}
            className={cn(
              "grid h-10 place-items-center rounded-control border border-line bg-surface font-numeric text-lg font-semibold text-ink-900",
              k === pressed && "border-brand-500 bg-brand-50 ring-4 ring-gold-500",
            )}
          >
            {k}
          </span>
        ) : (
          <span key={i} />
        ),
      )}
    </span>
  );
}

const NOTE_COLOURS: Record<number, string> = {
  10: "bg-money-500",
  20: "bg-warn-700",
  50: "bg-danger-500",
  100: "bg-accent-purple",
  200: "bg-accent-orange",
};

/** A banknote, by colour and amount: recognised long before it is read. */
export function Note({ rand, small }: { rand: 10 | 20 | 50 | 100 | 200; small?: boolean }) {
  return (
    <span
      className={cn(
        "grid place-items-center rounded-md border-2 border-white/60 font-extrabold text-white shadow-card",
        NOTE_COLOURS[rand],
        small ? "h-8 w-14 text-sm" : "h-12 w-20 text-lg",
      )}
    >
      R{rand}
    </span>
  );
}

/** A coin. */
export function Coin({ rand }: { rand: string }) {
  return (
    <span className="grid size-10 place-items-center rounded-full border-2 border-gold-700 bg-gold-500 text-xs font-extrabold text-navy-900 shadow-card">
      {rand}
    </span>
  );
}

/** A big tick or cross over a picture: do this, never this. */
export function Verdict({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <span className="relative inline-flex">
      {children}
      <span
        className={cn(
          "absolute -right-3 -top-3 grid size-9 place-items-center rounded-full text-white shadow-raised",
          ok ? "bg-money-500" : "bg-danger-500",
        )}
      >
        {ok ? (
          <Check className="size-5" strokeWidth={3} />
        ) : (
          <X className="size-5" strokeWidth={3} />
        )}
      </span>
    </span>
  );
}

/** The SmartSpaza icon on the device's home screen. */
export function AppIcon() {
  return (
    <span className="grid justify-items-center gap-1">
      <img src="/icons/icon-192.png" alt="" className="size-16 rounded-2xl shadow-raised" />
      <span className="text-xs font-medium text-ink-900">SmartSpaza</span>
    </span>
  );
}

/** A large symbol in a soft circle, for steps that are about the world, not the screen. */
export function Big({ children, tone = "brand" }: { children: ReactNode; tone?: Tone }) {
  return (
    <span className={cn("grid size-28 place-items-center rounded-full", TONES[tone].soft)}>
      <span className={cn("[&>svg]:size-14", TONES[tone].text)}>{children}</span>
    </span>
  );
}

export type Tone = "brand" | "money" | "gold" | "navy" | "warn" | "danger";

export const TONES: Record<Tone, { soft: string; text: string; solid: string }> = {
  brand: { soft: "bg-brand-50", text: "text-brand-500", solid: "bg-brand-500" },
  money: { soft: "bg-money-50", text: "text-money-700", solid: "bg-money-500" },
  gold: { soft: "bg-gold-50", text: "text-gold-700", solid: "bg-gold-500" },
  navy: { soft: "bg-canvas", text: "text-navy-800", solid: "bg-navy-800" },
  warn: { soft: "bg-warn-50", text: "text-warn-700", solid: "bg-warn-500" },
  danger: { soft: "bg-danger-50", text: "text-danger-700", solid: "bg-danger-500" },
};

export function Row({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center justify-center gap-3">{children}</div>;
}
